/* ============================================================
   INEXORABLE — Theme JS
   Cart drawer · Mega menu · Gallery · Size filter · Modals
   ============================================================ */

'use strict';

// ─── Utils ────────────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

// ─── Header scroll effect ─────────────────────────────────────
function initHeaderScroll() {
  const header = $('#SiteHeader');
  if (!header) return;
  const onScroll = debounce(() => {
    header.classList.toggle('scrolled', window.scrollY > 20);
  }, 50);
  window.addEventListener('scroll', onScroll, { passive: true });
}

// ─── Mega Menu ────────────────────────────────────────────────
function initMegaMenu() {
  const trigger = $('#CollectionsMenuTrigger');
  const menu = $('#CollectionsMegaMenu');
  if (!trigger || !menu) return;

  let closeTimeout;

  function openMenu() {
    clearTimeout(closeTimeout);
    trigger.setAttribute('aria-expanded', 'true');
    menu.setAttribute('aria-hidden', 'false');
  }

  function closeMenu() {
    closeTimeout = setTimeout(() => {
      trigger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
    }, 100);
  }

  trigger.addEventListener('mouseenter', openMenu);
  trigger.addEventListener('mouseleave', closeMenu);
  menu.addEventListener('mouseenter', () => clearTimeout(closeTimeout));
  menu.addEventListener('mouseleave', closeMenu);

  // Keyboard
  trigger.addEventListener('click', () => {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    expanded ? closeMenu() : openMenu();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });

  // Click outside
  document.addEventListener('click', (e) => {
    if (!trigger.contains(e.target) && !menu.contains(e.target)) closeMenu();
  });
}

// ─── Mobile Menu ──────────────────────────────────────────────
function initMobileMenu() {
  const toggle = $('#MobileMenuToggle');
  const menu = $('#MobileMenu');
  if (!toggle || !menu) return;

  function toggleMenu() {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isOpen));
    menu.setAttribute('aria-hidden', String(isOpen));
    document.body.style.overflow = isOpen ? '' : 'hidden';
  }

  toggle.addEventListener('click', toggleMenu);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.getAttribute('aria-hidden') === 'false') {
      toggleMenu();
    }
  });
}

// ─── Seuils livraison gratuite (en centimes EUR) ──────────────
const SHIPPING_THRESHOLDS = {
  'france-relay':     15000,
  'france-colissimo': 20000,
  'eu':               25000,
  'europe-hors-ue':   30000,
  'international':    50000
};

// Pays → zone (ISO 3166-1 alpha-2)
const COUNTRY_TO_ZONE = {
  FR: 'france-relay', MC: 'france-relay',
  // Union Européenne
  AT:'eu',BE:'eu',BG:'eu',HR:'eu',CY:'eu',CZ:'eu',DK:'eu',EE:'eu',
  FI:'eu',DE:'eu',GR:'eu',HU:'eu',IE:'eu',IT:'eu',LV:'eu',LT:'eu',
  LU:'eu',MT:'eu',NL:'eu',PL:'eu',PT:'eu',RO:'eu',SK:'eu',SI:'eu',
  ES:'eu',SE:'eu',
  // Europe hors UE
  GB:'europe-hors-ue',CH:'europe-hors-ue',NO:'europe-hors-ue',
  IS:'europe-hors-ue',LI:'europe-hors-ue',AL:'europe-hors-ue',
  BA:'europe-hors-ue',BY:'europe-hors-ue',MD:'europe-hors-ue',
  ME:'europe-hors-ue',MK:'europe-hors-ue',RS:'europe-hors-ue',
  UA:'europe-hors-ue',XK:'europe-hors-ue',AD:'europe-hors-ue',
  SM:'europe-hors-ue',VA:'europe-hors-ue',TR:'europe-hors-ue',
  RU:'europe-hors-ue',AM:'europe-hors-ue',AZ:'europe-hors-ue',
  GE:'europe-hors-ue'
};

// ─── Format monnaie (respecte la devise active) ───────────────
function formatMoney(euroCents) {
  const currency = window.Shopify?.currency?.active || 'EUR';
  const rate     = parseFloat(window.Shopify?.currency?.rate || 1);
  const amount   = (euroCents / 100) * rate;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency,
    minimumFractionDigits: 0, maximumFractionDigits: 2
  }).format(amount);
}

// ─── Rendu HTML des items du panier ──────────────────────────
function renderCartItems(cart) {
  if (cart.item_count === 0) {
    return `<div class="cart-drawer__empty">
      <p style="color:#9A9A9A;margin-bottom:1.5rem;">Votre panier est vide.</p>
      <a href="/collections/all" class="btn btn--outline">Découvrir les collections</a>
    </div>`;
  }
  return cart.items.map(item => {
    const img = item.featured_image?.url
      ? `<img src="${item.featured_image.url}&width=120" alt="${item.title}" class="cart-item__img" loading="lazy">`
      : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__variant">${item.variant_title}</p>` : '';
    return `<div class="cart-item" data-key="${item.key}">
      <a href="${item.url}" class="cart-item__image-link">${img}</a>
      <div class="cart-item__info">
        <div class="cart-item__top">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          <button class="cart-item__remove" data-key="${item.key}" aria-label="Retirer du panier">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
          </button>
        </div>
        ${variant}
        <div class="cart-item__bottom">
          <div class="cart-item__qty">
            <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Diminuer">−</button>
            <span>${item.quantity}</span>
            <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Augmenter">+</button>
          </div>
          <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
        </div>
      </div>
    </div>`;
  }).join('');
}

// ─── Zone auto depuis le sélecteur de pays ────────────────────
function detectZoneFromCountry() {
  const countrySelect = $('#CountrySelect') || $('#CountrySelectMobile');
  const iso = countrySelect?.value || '';
  return COUNTRY_TO_ZONE[iso] || null;
}

// ─── Barre de progression livraison ──────────────────────────
function updateShippingProgress(totalCents) {
  const wrap = $('#ShippingProgressWrap');
  const fill = $('#ShippingProgressFill');
  const msg  = $('#ShippingProgressMsg');
  if (!wrap || !fill || !msg) return;

  // Auto-détecter la zone depuis le pays sélectionné
  const autoZone = detectZoneFromCountry();
  const saved    = localStorage.getItem('inexorable_shipping_zone');
  const zone     = saved || autoZone || 'france-relay';

  const zoneSelect = $('#ShippingZoneSelect');
  if (zoneSelect) zoneSelect.value = zone;

  // Convertir le seuil dans la devise active
  const rate           = parseFloat(window.Shopify?.currency?.rate || 1);
  const thresholdEur   = SHIPPING_THRESHOLDS[zone] || 15000;
  const thresholdDisp  = thresholdEur * rate;
  const totalDisp      = totalCents * rate;
  const pct = Math.min(100, Math.round((totalDisp / thresholdDisp) * 100));
  fill.style.width = pct + '%';

  if (totalDisp >= thresholdDisp) {
    msg.textContent = '🎉 Livraison offerte !';
    msg.style.color = '#C41230';
  } else {
    const remaining = formatMoney(thresholdEur - totalCents);
    msg.textContent = `Plus que ${remaining} pour la livraison gratuite`;
    msg.style.color = '#9A9A9A';
  }
}

// ─── Upsell produit ──────────────────────────────────────────
async function renderCartUpsell(cart) {
  const upsellWrap = $('#CartUpsell');
  if (!upsellWrap) return;
  const handle = window.InexorableConfig?.upsellHandle || '';
  if (!handle) { upsellWrap.style.display = 'none'; return; }

  // Masquer si le produit est déjà dans le panier
  const alreadyIn = cart.items.some(i => i.handle === handle);
  if (alreadyIn) { upsellWrap.style.display = 'none'; return; }

  try {
    const res     = await fetch(`/products/${handle}.js`);
    const product = await res.json();
    const variant = product.variants[0];
    const img     = product.featured_image
      ? `<img src="${product.featured_image}" class="cart-upsell__img" alt="${product.title}" loading="lazy">`
      : `<div class="cart-upsell__img"></div>`;

    upsellWrap.innerHTML = `
      <p class="cart-upsell__heading">Complétez votre panier</p>
      <div class="cart-upsell__inner">
        ${img}
        <div class="cart-upsell__info">
          <p class="cart-upsell__title">${product.title}</p>
          <p class="cart-upsell__price">${formatMoney(variant.price)}</p>
        </div>
        <button class="cart-upsell__btn" data-variant-id="${variant.id}" aria-label="Ajouter au panier">+ Ajouter</button>
      </div>`;
    upsellWrap.style.display = 'block';
  } catch (e) {
    upsellWrap.style.display = 'none';
  }
}

// ─── Mise à jour complète de l'UI panier ─────────────────────
function updateCartUI(cart) {
  // Badge
  const badge = $('#CartBadge');
  if (badge) {
    badge.textContent = cart.item_count;
    badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
  }

  // Drawer body
  const body = $('#CartDrawerBody');
  if (body) body.innerHTML = renderCartItems(cart);

  // Footer
  const footer = $('#CartDrawerFooter');
  const total  = $('#CartTotal');
  const progressWrap = $('#ShippingProgressWrap');

  if (footer) footer.style.display = cart.item_count > 0 ? 'flex' : 'none';
  if (total)  total.textContent = formatMoney(cart.total_price);
  if (progressWrap) progressWrap.style.display = cart.item_count > 0 ? 'block' : 'none';

  updateShippingProgress(cart.total_price);
  renderCartUpsell(cart);
}

// ─── Cart Drawer ──────────────────────────────────────────────
function initCartDrawer() {
  const cartOpen    = $('#CartOpen');
  const cartDrawer  = $('#CartDrawer');
  const cartOverlay = $('#CartOverlay');
  const cartClose   = $('#CartDrawerClose');
  if (!cartDrawer) return;

  async function openCart() {
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartOverlay) cartOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Toujours recharger le panier à l'ouverture
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCartUI(cart);
    } catch (e) { console.error('Cart fetch error', e); }
    cartClose?.focus();
  }

  function closeCart() {
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartOverlay) cartOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  cartOpen?.addEventListener('click', openCart);
  cartClose?.addEventListener('click', closeCart);
  cartOverlay?.addEventListener('click', closeCart);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer.getAttribute('aria-hidden') === 'false') closeCart();
  });

  // Zone de livraison
  const zoneSelect = $('#ShippingZoneSelect');
  zoneSelect?.addEventListener('change', async () => {
    localStorage.setItem('inexorable_shipping_zone', zoneSelect.value);
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateShippingProgress(cart.total_price);
    } catch (e) {}
  });

  // Délégation — qty dans le drawer
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.qty-btn[data-key]');
    if (!btn || !$('#CartDrawer')) return;
    const key    = btn.dataset.key;
    const action = btn.dataset.action;
    if (!key) return;

    const qtySpan = btn.closest('.cart-item__qty')?.querySelector('span');
    const current = parseInt(qtySpan?.textContent || '1', 10);
    const newQty  = action === 'increase' ? current + 1 : Math.max(0, current - 1);

    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      const cart = await res.json();
      updateCartUI(cart);
      if (window.location.pathname === '/cart') window.location.reload();
    } catch (err) { console.error('Cart change failed', err); }
  });

  // Suppression ligne — drawer panier et page panier
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.cart-item__remove[data-key], .cart-line__remove[data-key]');
    if (!btn) return;
    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: btn.dataset.key, quantity: 0 })
      });
      const cart = await res.json();
      updateCartUI(cart);
      if (window.location.pathname === '/cart') window.location.reload();
    } catch (err) { console.error('Cart remove failed', err); }
  });

  // Ajout upsell au panier
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.cart-upsell__btn[data-variant-id]');
    if (!btn) return;
    btn.textContent = '...';
    btn.disabled = true;
    try {
      const res  = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(btn.dataset.variantId), quantity: 1 })
      });
      await res.json();
      const cartRes  = await fetch('/cart.js');
      const cart     = await cartRes.json();
      updateCartUI(cart);
    } catch (err) {
      btn.textContent = '+';
      btn.disabled = false;
    }
  });
}

// ─── Quick Add ────────────────────────────────────────────────
function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.product-card__quick-btn[data-variant-id]');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.textContent = '…';
    btn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('Add failed');

      btn.textContent = '✓ Ajouté';
      btn.style.backgroundColor = '#C41230';
      btn.style.color = '#fff';

      const cartRes = await fetch('/cart.js');
      const cart    = await cartRes.json();
      updateCartUI(cart);

      // Ouvrir le drawer
      const drawer  = $('#CartDrawer');
      const overlay = $('#CartOverlay');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }

      setTimeout(() => {
        btn.textContent = 'Ajouter au panier';
        btn.style.backgroundColor = '';
        btn.style.color = '';
        btn.disabled = false;
      }, 2500);
    } catch (err) {
      btn.textContent = 'Erreur';
      setTimeout(() => { btn.textContent = 'Ajouter au panier'; btn.disabled = false; }, 2000);
    }
  });
}

// ─── Product Form ─────────────────────────────────────────────
function initProductForm() {
  const form = document.querySelector('.product-form');
  if (!form) return;

  // Quantity controls
  const qtyInput = $('#Quantity');
  const qtyDecrease = $('#QtyDecrease');
  const qtyIncrease = $('#QtyIncrease');

  function getMaxQty() {
    return parseInt(qtyInput?.dataset.max || '99', 10);
  }

  qtyDecrease?.addEventListener('click', () => {
    const val = parseInt(qtyInput.value, 10);
    if (val > 1) {
      qtyInput.value = val - 1;
      if (qtyIncrease) qtyIncrease.disabled = false;
    }
  });

  qtyIncrease?.addEventListener('click', () => {
    const val = parseInt(qtyInput.value, 10);
    const max = getMaxQty();
    if (val < max) {
      qtyInput.value = val + 1;
      if (val + 1 >= max) qtyIncrease.disabled = true;
    }
  });

  // Init état du bouton +
  if (qtyInput && qtyIncrease) {
    const max = getMaxQty();
    if (parseInt(qtyInput.value, 10) >= max) qtyIncrease.disabled = true;
  }

  // Variant selection
  $$('.variant-option__input, .size-option__input').forEach(input => {
    input.addEventListener('change', updateSelectedVariant);
  });

  // Add to cart
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = $('#AddToCart');
    const errorEl = $('#ProductFormError');
    const variantId = $('#VariantId')?.value;

    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.disabled = true;
    if (errorEl) errorEl.hidden = true;

    try {
      const qty = parseInt(qtyInput?.value || '1', 10);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Impossible d\'ajouter cet article.');
      }

      // Success — open cart drawer
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      updateCartUI(cart);

      const cartDrawer = $('#CartDrawer');
      const cartOverlay = $('#CartOverlay');
      if (cartDrawer) {
        cartDrawer.setAttribute('aria-hidden', 'false');
        if (cartOverlay) cartOverlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }

    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  });
}

function updateSelectedVariant() {
  const productData = window.__productData;
  if (!productData) return;

  const selectedOptions = $$('.product-form [name^="options"]')
    .filter(input => input.type === 'radio' ? input.checked : true)
    .map(input => input.value);

  const matchedVariant = productData.variants.find(v =>
    v.options.every((opt, i) => opt === selectedOptions[i])
  );

  if (!matchedVariant) return;

  const variantInput = $('#VariantId');
  const priceEl = $('#ProductPrice');
  const addBtn = $('#AddToCart');
  const addBtnText = $('#AddToCartText');

  if (variantInput) variantInput.value = matchedVariant.id;

  if (priceEl) {
    if (matchedVariant.compare_at_price > matchedVariant.price) {
      priceEl.innerHTML = `
        <span class="price price--compare">${formatMoney(matchedVariant.compare_at_price)}</span>
        <span class="price price--sale">${formatMoney(matchedVariant.price)}</span>
      `;
    } else {
      priceEl.innerHTML = `<span class="price">${formatMoney(matchedVariant.price)}</span>`;
    }
  }

  if (addBtn && addBtnText) {
    if (matchedVariant.available) {
      addBtn.disabled = false;
      addBtn.removeAttribute('aria-disabled');
      addBtnText.textContent = 'Ajouter au panier';
    } else {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      addBtnText.textContent = 'Épuisé';
    }
  }

  // Mettre à jour le stock max pour le contrôle quantité
  const qtyInput    = $('#Quantity');
  const qtyIncrease = $('#QtyIncrease');
  if (qtyInput) {
    const maxQty = matchedVariant.inventory_quantity > 0 ? matchedVariant.inventory_quantity : 99;
    qtyInput.dataset.max = maxQty;
    if (qtyInput.value > maxQty) qtyInput.value = maxQty;
    if (qtyIncrease) qtyIncrease.disabled = parseInt(qtyInput.value, 10) >= maxQty;
  }

  // Update URL
  const url = new URL(window.location.href);
  url.searchParams.set('variant', matchedVariant.id);
  window.history.replaceState({}, '', url.toString());
}

// ─── Product Gallery ──────────────────────────────────────────
function initProductGallery() {
  const thumbs = $$('.gallery-thumb');
  const slides = $$('.gallery-slide');
  const prevBtn = $('#GalleryPrev');
  const nextBtn = $('#GalleryNext');

  if (!slides.length) return;

  let current = 0;

  function goTo(index) {
    slides[current]?.classList.remove('gallery-slide--active');
    thumbs[current]?.classList.remove('gallery-thumb--active');

    current = (index + slides.length) % slides.length;

    slides[current]?.classList.add('gallery-slide--active');
    thumbs[current]?.classList.add('gallery-thumb--active');
    thumbs[current]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => goTo(i));
  });

  prevBtn?.addEventListener('click', () => goTo(current - 1));
  nextBtn?.addEventListener('click', () => goTo(current + 1));

  // Touch swipe on mobile
  let touchStartX = 0;
  const mainGallery = $('#GalleryMain');
  mainGallery?.addEventListener('touchstart', e => { touchStartX = e.changedTouches[0].screenX; }, { passive: true });
  mainGallery?.addEventListener('touchend', e => {
    const diff = touchStartX - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 50) goTo(diff > 0 ? current + 1 : current - 1);
  }, { passive: true });
}

// ─── Collection filters ───────────────────────────────────────
function initCollectionFilters() {
  // Filter group toggles
  $$('.filter-group__toggle').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const targetId = toggle.getAttribute('aria-controls');
      const target = targetId ? document.getElementById(targetId) : toggle.nextElementSibling;

      toggle.setAttribute('aria-expanded', String(!expanded));
      if (target) target.hidden = expanded;
    });
  });

  // Filter panel toggle (mobile + desktop)
  const filtersToggle = $('#FiltersToggle');
  const filtersSidebar = $('#CollectionFilters');
  const filtersBody = filtersSidebar?.closest('.collection-page__body');
  filtersToggle?.addEventListener('click', () => {
    const isOpen = filtersToggle.getAttribute('aria-expanded') === 'true';
    filtersToggle.setAttribute('aria-expanded', String(!isOpen));
    filtersSidebar?.classList.toggle('is-open', !isOpen);
    filtersBody?.classList.toggle('filters-open', !isOpen);
  });

  // Clear all filters
  const clearBtn = $('#FiltersClear');
  clearBtn?.addEventListener('click', () => {
    $$('.size-filter-input:checked, .filter-option input:checked').forEach(input => {
      input.checked = false;
    });
    submitFilters();
  });

  // Restaurer l'état des filtres taille depuis l'URL
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.getAll('filter.p.option.taille').forEach(val => {
    const input = document.querySelector(`.size-filter-input[value="${val}"]`);
    if (input) input.checked = true;
  });

  // Auto-submit on size filter change
  $$('.size-filter-input').forEach(input => {
    input.addEventListener('change', submitFilters);
  });

  // Auto-submit on native filter change
  $$('.filter-option input').forEach(input => {
    input.addEventListener('change', submitFilters);
  });

  // Sort change
  const sortSelect = $('#SortBy');
  sortSelect?.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });
}

function submitFilters() {
  const url = new URL(window.location.href);

  // Clear existing filter params
  [...url.searchParams.keys()]
    .filter(k => k.startsWith('filter.'))
    .forEach(k => url.searchParams.delete(k));

  // Add active size filters
  $$('.size-filter-input:checked').forEach(input => {
    url.searchParams.append('filter.p.option.taille', input.value);
  });

  // Add other native filters
  $$('.filter-option input:checked').forEach(input => {
    url.searchParams.append(input.name, input.value);
  });

  // Add price range
  const priceMin = $('#PriceMin');
  const priceMax = $('#PriceMax');
  if (priceMin && priceMin.value !== '') url.searchParams.set('filter.v.price.gte', priceMin.value);
  if (priceMax && priceMax.value !== '') url.searchParams.set('filter.v.price.lte', priceMax.value);

  window.location.href = url.toString();
}

// ─── Testimonials Carousel ────────────────────────────────────
function initTestimonials() {
  const inner = $('#TestimonialsInner');
  const prevBtn = $('#TestimonialsPrev');
  const nextBtn = $('#TestimonialsNext');
  const dotsContainer = $('#TestimonialsDots');
  if (!inner || !prevBtn) return;

  const cards = $$('.review-card', inner);
  if (cards.length <= 3) return;

  const perPage = window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
  const totalPages = Math.ceil(cards.length / perPage);
  let currentPage = 0;

  // Build dots
  if (dotsContainer) {
    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = `testimonials__dot${i === 0 ? ' testimonials__dot--active' : ''}`;
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Avis ${i + 1}`);
      dot.setAttribute('aria-selected', String(i === 0));
      dot.addEventListener('click', () => goToPage(i));
      dotsContainer.appendChild(dot);
    }
  }

  function goToPage(page) {
    currentPage = (page + totalPages) % totalPages;
    const offset = currentPage * (100 / perPage) * perPage;
    inner.style.transform = `translateX(-${offset / perPage}%)`;

    $$('.testimonials__dot', dotsContainer).forEach((dot, i) => {
      dot.classList.toggle('testimonials__dot--active', i === currentPage);
      dot.setAttribute('aria-selected', String(i === currentPage));
    });
  }

  prevBtn.addEventListener('click', () => goToPage(currentPage - 1));
  nextBtn?.addEventListener('click', () => goToPage(currentPage + 1));

  // Auto-advance
  let autoPlay = setInterval(() => goToPage(currentPage + 1), 5000);
  inner.addEventListener('mouseenter', () => clearInterval(autoPlay));
  inner.addEventListener('mouseleave', () => { autoPlay = setInterval(() => goToPage(currentPage + 1), 5000); });
}

// ─── Size Guide Modal ─────────────────────────────────────────
function initSizeGuide() {
  const openBtn = $('#SizeGuideBtn');
  const modal = $('#SizeGuideModal');
  const overlay = $('#SizeGuideOverlay');
  const closeBtn = $('#SizeGuideClose');
  if (!openBtn || !modal) return;

  function open() {
    modal.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.setAttribute('aria-hidden', 'false');
    closeBtn?.focus();
    document.body.style.overflow = 'hidden';
  }

  function close() {
    modal.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    openBtn.focus();
  }

  openBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') close(); });
}

// ─── Search Modal ─────────────────────────────────────────────
function initSearch() {
  const openBtn = $('#SearchOpen');
  const modal = $('#SearchModal');
  const overlay = $('#SearchOverlay');
  const closeBtn = $('#SearchModalClose');
  const input = modal?.querySelector('.search-modal__input');
  if (!openBtn || !modal) return;

  function open() {
    modal.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => input?.focus(), 100);
  }

  function close() {
    modal.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  openBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') close(); });
}

// ─── Pays / devise ────────────────────────────────────────────
function initCountrySelector() {
  $$('[data-localization-select]').forEach(sel => {
    sel.addEventListener('change', function() {
      const f = document.createElement('form');
      f.method = 'post';
      f.action = '/localization';
      [['form_type', 'localization'], ['utf8', '✓'], ['_method', 'put'],
       ['return_to', window.location.pathname], ['country_code', this.value]
      ].forEach(([name, value]) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = name; i.value = value;
        f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    });
  });
}

// ─── Produits similaires ──────────────────────────────────────
function initProductRecommendations() {
  const wrap = $('#ProductRecommendations');
  if (!wrap) return;

  // Already server-rendered (AJAX context)
  if (wrap.querySelector('.product-recommendations__inner')) return;

  const productId = wrap.dataset.productId;
  const limit     = Number(wrap.dataset.limit) || 4;
  const sectionId = wrap.dataset.sectionId;
  const baseUrl   = wrap.dataset.url;
  if (!productId || !sectionId || !baseUrl) return;

  fetch(`${baseUrl}?product_id=${productId}&limit=${limit * 3}&section_id=${sectionId}`)
    .then(r => r.text())
    .then(html => {
      const doc   = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.product-recommendations__inner');
      if (!inner) { wrap.style.display = 'none'; return; }

      // Fisher-Yates shuffle then trim to limit
      const grid  = inner.querySelector('.product-recommendations__grid');
      if (grid) {
        const cards = [...grid.querySelectorAll('.product-card')];
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        grid.innerHTML = '';
        cards.slice(0, limit).forEach(c => grid.appendChild(c));
      }

      wrap.innerHTML = inner.outerHTML;
    })
    .catch(() => { wrap.style.display = 'none'; });
}

// ─── Init ─────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initHeaderScroll();
  initMegaMenu();
  initMobileMenu();
  initCartDrawer();
  initQuickAdd();
  initProductForm();
  initProductGallery();
  initCollectionFilters();
  initTestimonials();
  initSizeGuide();
  initSearch();
  initProductRecommendations();
  initCountrySelector();
});
